// What the kind and the class views know of the value chain around them: the
// chain the page shows, if the overlay lies over the value chain page, and
// how a root is made. Also the address of the value chain with a root.

import { createContext, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { ValueChainParams } from "../../services/productPlaneApi";
import {
  ChainCard,
  ChainCardType,
  ChainDirection,
  ValueChain,
} from "../../types/valueChain";
import {
  ChainCardRef,
  ChainDetailRef,
  DEFAULT_VALUE_CHAIN_STATE,
  DETAIL_TYPES,
  defaultDirection,
  encodeValueChainState,
} from "../ValueChain/valueChainState";

export interface PageChain {
  // The chain the page shows; null while it loads, or on another page
  chain: ValueChain | null;
  // Makes a card the root of the page's chain, in place: read in the
  // direction given, else in the root's default one
  onMakeRoot: (root: ChainCardRef, direction?: ChainDirection) => void;
}

// Given by the value chain page around its overlay; null on every other page
export const PageChainContext = createContext<PageChain | null>(null);

export const usePageChain = (): PageChain | null => useContext(PageChainContext);

// The address of the value chain page with a root, as the page writes it:
// read in the direction given, else in the root's default one
export const valueChainAddress = (
  root: ChainCardRef,
  direction: ChainDirection = defaultDirection(root),
): string =>
  `/value-chain?${encodeValueChainState({
    ...DEFAULT_VALUE_CHAIN_STATE,
    root,
    direction,
  }).toString()}`;

// Makes a kind or a class the root of the value chain, in the direction given
export type MakeRoot = (root: ChainCardRef, direction?: ChainDirection) => void;

/**
 * Makes a kind or a class the root of the value chain: in place on the value
 * chain page, else by going to the page with the root in the address. A
 * direction is given to read the root to one side; without one the root is
 * read in its default direction.
 */
export const useMakeRoot = (): MakeRoot => {
  const page = usePageChain();
  const navigate = useNavigate();
  return useCallback(
    (root: ChainCardRef, direction?: ChainDirection) => {
      if (page) {
        if (direction) {
          page.onMakeRoot(root, direction);
        } else {
          page.onMakeRoot(root);
        }
      } else {
        navigate(valueChainAddress(root, direction));
      }
    },
    [page, navigate],
  );
};

// The read that fills the kind's or the class's chain sections: one step to
// each side
export const neighbourParams = (
  type: ChainCardType,
  categoryId: string,
): ValueChainParams => ({
  root_type: type,
  root_id: categoryId,
  direction: "both",
  parts_depth: 1,
  hosts_depth: 1,
});

// What a card is called: its label, else its id
export const cardName = (card: ChainCard): string => card.label ?? card.id;

// Every card of a chain, the root first
export const chainCards = (chain: ValueChain): ChainCard[] => [
  chain.root,
  ...chain.cards,
];

// The card of a chain that stands for a category; undefined when none does
export const cardOfCategory = (
  chain: ValueChain | null | undefined,
  categoryId: string,
): ChainCard | undefined =>
  chain
    ? chainCards(chain).find((card) => card.category_id === categoryId)
    : undefined;

// Whether a chain holds a card, read from another chain: by its id, or by the
// category it stands for
export const isInChain = (
  chain: ValueChain | null | undefined,
  card: ChainCard,
): boolean =>
  chain
    ? chainCards(chain).some(
        (entry) =>
          entry.id === card.id || entry.category_id === card.category_id,
      )
    : false;

// The detail a card of the page opens: the kind or the class it stands for,
// as one of the page's reads has it. A category no card stands for stays a
// category.
export const chainDetailOf = (
  detail: ChainDetailRef,
  chains: readonly (ValueChain | null | undefined)[],
): ChainDetailRef => {
  if (detail.type !== "category") {
    return detail;
  }
  for (const chain of chains) {
    const card = cardOfCategory(chain, detail.id);
    if (card) {
      return { type: card.type, id: detail.id };
    }
  }
  return detail;
};

// The detail an entity click names; null for a type the page cannot hold
export const toChainDetail = (
  id: string,
  type: string | undefined,
): ChainDetailRef | null => {
  const found = DETAIL_TYPES.find((allowed) => allowed === type);
  return found ? { type: found, id } : null;
};

// What every count of the kind and the class views is
export const ON_RECORD = "Counts are on record, never totals.";

// Names joined as a list with "or": "a", "a or b", "a, b or c"
const orList = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;

// A count with its noun, the noun in the plural unless the count is one
const counted = (count: number, noun: string): string =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

/**
 * How a card sits in the chain the page shows, in words: stated inside its
 * neighbours nearer the root on the parts side, holding them on the hosts
 * side, by the products of it stated with theirs.
 */
export const chainPlaceLine = (
  chain: ValueChain,
  categoryId: string,
): string => {
  if (chain.root.category_id === categoryId) {
    return "In this chain: the root.";
  }
  const card = cardOfCategory(chain, categoryId);
  if (!card) {
    return "Not in this chain.";
  }
  // A path without an intermediate card comes straight from the root
  const nearer = Array.from(
    new Set(
      card.paths.map((path) =>
        path.via ? (path.via_label ?? path.via) : cardName(chain.root),
      ),
    ),
  );
  if (nearer.length === 0 || card.side === null) {
    return "In this chain.";
  }
  // A card reached by typical-host rows alone has no stated product
  if (card.stated_count === 0 && card.typical_count > 0) {
    return card.side === "parts"
      ? `In this chain: typically built into ${orList(nearer)}.`
      : `In this chain: ${orList(nearer)} is typically built into it.`;
  }
  const products = counted(card.stated_count, "product");
  return card.side === "parts"
    ? `In this chain: stated inside ${orList(nearer)} by ${products}.`
    : `In this chain: holds ${orList(nearer)} in ${products}.`;
};
